/**
 * 认证管理模块（基于 HttpOnly Cookie）
 */

export interface CurrentUser {
  id?: number | string;
  username?: string;
  email?: string;
  // 说明：后端 /api/user/info 不返回 is_admin 字段；管理员身份仅通过 JWT
  // 中的 is_admin claim 在服务端校验（见 middleware.AdminAuthMiddleware）。
  // 前端如需判断是否管理员，应改为调用一个受 admin 中间件保护的探测接口。
  [key: string]: unknown;
}

const API_BASE = `${window.location.origin}/api`;

let currentUser: CurrentUser | null = null;
let loadPromise: Promise<CurrentUser | null> | null = null;

export async function loadUser(): Promise<CurrentUser | null> {
  if (loadPromise) return loadPromise;
  loadPromise = (async () => {
    try {
      const resp = await fetch(`${API_BASE}/user/info`, {
        method: 'GET',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (resp.status === 401) {
        // Token 过期或无效：清除状态，不输出错误日志（这是预期行为）
        currentUser = null;
        return null;
      }
      if (!resp.ok) {
        currentUser = null;
        return null;
      }
      const data = (await resp.json()) as { code?: number; data?: CurrentUser };
      if (data && data.code === 200 && data.data) {
        currentUser = data.data;
        return currentUser;
      }
      currentUser = null;
      return null;
    } catch (e) {
      console.warn('[Auth] loadUser failed:', e);
      currentUser = null;
      return null;
    } finally {
      loadPromise = null;
    }
  })();
  return loadPromise;
}

export function isLoggedIn(): boolean {
  return !!currentUser;
}

export function getCurrentUser(): CurrentUser | null {
  return currentUser;
}

export function setUser(user: CurrentUser | null): void {
  currentUser = user ?? null;
}

export function clearUser(): void {
  currentUser = null;
}

export async function logout(): Promise<void> {
  try {
    await fetch(`${API_BASE}/auth/logout`, {
      method: 'POST',
      credentials: 'same-origin',
    });
  } catch (e) {
    console.warn('[Auth] logout request failed:', e);
  }
  clearUser();
  window.location.href = '/';
}

export function isAdmin(): boolean {
  // 已弃用：始终返回 false。
  // 后端 /api/user/info 不返回 is_admin；管理员权限仅由 JWT 校验。
  // 保留空实现只是为了不破坏可能的外部引用，未来可直接删除。
  return false;
}

export const AuthModule = {
  loadUser,
  isLoggedIn,
  getCurrentUser,
  setUser,
  clearUser,
  logout,
  isAdmin,
};

// 兼容层
declare global {
  interface Window {
    AuthModule?: typeof AuthModule;
  }
}
window.AuthModule = AuthModule;
